/**
 * Nível de áudio do domo — o ponto de encontro entre a pilha de voz e a casca.
 *
 * O domo pulsa com a amplitude do áudio corrente. Quem mede é o módulo de voz
 * (analisador do microfone na escuta, do player na fala); quem desenha é o
 * domo. Um store minúsculo fora do React evita que a casca precise conhecer o
 * módulo de voz, e evita re-render em cascata a 60 Hz: só quem chama o hook
 * redesenha.
 *
 * Contrato para o módulo de voz: chamar `publicarNivelAudio(n)` com `n` em
 * 0..1 na cadência que quiser, e `publicarNivelAudio(0)` ao parar.
 */

import { useSyncExternalStore } from 'react';

let nivel = 0;
const ouvintes = new Set<() => void>();

export function publicarNivelAudio(valor: number): void {
  // Fora de 0..1 o domo distorce; grampear aqui é mais barato que confiar em
  // todos os produtores.
  const normalizado = Number.isFinite(valor) ? Math.min(1, Math.max(0, valor)) : 0;
  // Arredondar em centésimos corta re-renders imperceptíveis: um analisador de
  // áudio emite variação na sexta casa a cada quadro.
  const arredondado = Math.round(normalizado * 100) / 100;
  if (arredondado === nivel) return;
  nivel = arredondado;
  for (const ouvinte of ouvintes) ouvinte();
}

function assinar(aoMudar: () => void): () => void {
  ouvintes.add(aoMudar);
  return () => {
    ouvintes.delete(aoMudar);
  };
}

function ler(): number {
  return nivel;
}

export function useNivelAudio(): number {
  // O terceiro argumento serve a uma eventual renderização no servidor; aqui
  // devolve o mesmo valor inicial e mantém o hook satisfeito.
  return useSyncExternalStore(assinar, ler, () => 0);
}
